import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  asTeamDocumentId,
  asTeamProjectId,
} from '@nimbalyst/collab-bundle/editor';
import {
  copyTextToClipboard,
  getRecordTitle,
  TrackerItemDetailPanel,
  TrackerMutationRejectionNotice,
  TrackerSurfaceMessage,
  useTrackerCommand,
  useTrackerDataSelector,
  type TeamMemberOption,
  type TrackerItemAction,
} from '@nimbalyst/collab-bundle/trackers-ui';
import type { TeamMember } from '../api';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { CollabEditorMount } from '../collab/CollabEditorMount';
import {
  CollabDocumentStatusIndicator,
  CollabDocumentStatusProvider,
  useCollabDocumentStatus,
} from '../collab/documentStatus';
import { memberDisplayName } from '../collab/orgRoster';
import { consoleConfig } from '../config';
import { orgTrackerItemPath, orgTrackersPath, orgTrackerTypePath } from '../routing';
import { useTrackersShell, useTrackerSelection } from './TrackersShell';
import { usePhoneTrackerLayout } from './usePhoneTrackerLayout';
import { useTrackerCommentDraft } from './TrackerBrowseState';

/**
 * A tracker item's body is an ordinary collaborative document.
 *
 * The desktop names it `tracker-content/<itemId>` in the same project room the
 * shared documents use, so the browser opens the identical room through the
 * identical mount. This is deliberately not a second editor integration: the
 * cold-paint contract -- the binding only paints Y.Doc events observed after it
 * mounts (NIM-1764) -- belongs to `CollabEditorMount`, and a second
 * implementation would be a second chance to get it wrong.
 */
export function trackerBodyDocumentId(itemId: string): string {
  return `tracker-content/${itemId}`;
}

/**
 * The body's connectivity, drawn on the item header rather than on a strip of
 * its own.
 *
 * A leaf on purpose. `CollabDocumentStatusProvider` re-renders on every status
 * the mounted editor publishes, and only this component subscribes -- the
 * sibling that holds `bodySlot` is passed to the provider as `children`, so
 * React bails out of re-rendering it entirely and the live Y.Doc binding never
 * sees a reconcile it did not need. Reading the status inside `TrackerItemSurface`
 * itself would rebuild that element on every connection blip.
 */
function ItemBodyStatus() {
  return <CollabDocumentStatusIndicator status={useCollabDocumentStatus()} />;
}

export function TrackerItemSurface({
  itemId,
  members,
  teamSession,
  presenceName,
  getTeamJwt,
}: {
  itemId: string;
  members: readonly TeamMember[];
  /** Carries the branded org and member ids the team room refuses to take raw. */
  teamSession: TeamSession;
  presenceName: string;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
}) {
  const { team, projectId, identity, navigate, trackerType, viewId, schema } = useTrackersShell();
  const recordsById = useTrackerDataSelector(state => state.recordsById);
  const loaded = useTrackerDataSelector(state => state.loaded);
  const sync = useTrackerDataSelector(state => state.sync);
  const rejection = useTrackerDataSelector(state => state.rejection);
  const command = useTrackerCommand();
  const [mutationError, setMutationError] = useState<string | null>(null);
  /**
   * What an overflow action just did.
   *
   * A clipboard write changes nothing on screen and throws nothing when the
   * browser denies it, so an action that only writes the clipboard is
   * indistinguishable from one that did nothing at all. The header's copy-link
   * button carries its own confirmation; menu items close on select and have
   * nowhere to put one, so they report here.
   */
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const item = recordsById.get(itemId) ?? null;
  const phone = usePhoneTrackerLayout();
  const [editState, setEditState] = useState(() => ({ itemId, editing: !phone }));
  if (editState.itemId !== itemId) setEditState({ itemId, editing: !phone });
  const editing = editState.itemId === itemId ? editState.editing : !phone;
  const [commentDraft, setCommentDraft] = useTrackerCommentDraft(itemId);
  const { views } = useTrackerSelection();
  const returnType = trackerType || item?.primaryType;
  const returnLabel = viewId ? views.find(view => view.id === viewId)?.name : null;
  const returnTypeModel = schema.trackerTypes.find(model => model.type === returnType);
  const backLabel = `Back to ${returnLabel || returnTypeModel?.displayNamePlural || returnTypeModel?.displayName || 'trackers'}`;

  // Closing drops the item, not the list: it returns to whichever list the item
  // was opened from rather than to every tracker in the project. Computed above
  // the surface's early returns because the Escape handler below is a hook.
  const closePath = viewId
    ? orgTrackersPath(team, projectId, viewId)
    : returnType && returnType !== 'all'
      ? orgTrackerTypePath(team, projectId, returnType)
      : orgTrackersPath(team, projectId);
  const closePathRef = useRef(closePath);
  closePathRef.current = closePath;

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  /**
   * Escape closes the open item, the way it closes every floating surface in
   * this console.
   *
   * Bubble phase and heavily guarded, because the thing filling most of this
   * pane is a live Lexical editor. Escape inside a body, a field editor or the
   * comment composer means "cancel what I am doing" and must never mean "throw
   * the pane away"; `defaultPrevented` covers a handler below that already
   * claimed it, and the `activeElement` check covers the ones that do not.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && (
        active.isContentEditable
        || active.tagName === 'INPUT'
        || active.tagName === 'TEXTAREA'
        || active.tagName === 'SELECT'
      )) return;
      navigate(closePathRef.current);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  const copyValue = useCallback(async (value: string, copied: string) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    try {
      await copyTextToClipboard(value);
      setActionNotice(copied);
    } catch {
      setActionNotice('This browser would not let Nimbalyst write to the clipboard.');
    }
    noticeTimerRef.current = setTimeout(() => setActionNotice(null), 2400);
  }, []);

  const updateField = useCallback(async (fieldName: string, value: unknown) => {
    setMutationError(null);
    try {
      return await command({
        type: 'update-item',
        input: { itemId, updates: { [fieldName]: value }, sharing: 'team' },
      });
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setMutationError(message);
      throw cause;
    }
  }, [command, itemId]);

  const mutateComment = useCallback(async (mutation: {
    kind: 'add' | 'update' | 'delete';
    body?: string;
    commentId?: string;
  }) => {
    setMutationError(null);
    try {
      if (mutation.kind === 'add') {
        return await command({ type: 'add-comment', itemId, body: mutation.body ?? '' });
      }
      return await command({
        type: 'update-comment',
        itemId,
        commentId: mutation.commentId ?? '',
        ...(mutation.kind === 'update' ? { body: mutation.body ?? '' } : { deleted: true }),
      });
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setMutationError(message);
      throw cause;
    }
  }, [command, itemId]);

  const source = useMemo(() => ({
    kind: 'team-room' as const,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    room: {
      orgId: teamSession.orgId,
      projectId: asTeamProjectId(projectId),
      documentId: asTeamDocumentId(trackerBodyDocumentId(itemId)),
    },
    auth: {
      scope: 'team' as const,
      memberId: teamSession.memberId,
      getTeamJwt,
    },
  }), [teamSession.orgId, teamSession.memberId, projectId, itemId, getTeamJwt]);

  const user = useMemo(
    () => ({ memberId: teamSession.memberId, name: presenceName }),
    [teamSession.memberId, presenceName],
  );

  const teamMembers = useMemo<TeamMemberOption[]>(
    () => members.map((member) => ({ email: member.email, name: memberDisplayName(member) })),
    [members],
  );

  const desktopHref = useMemo(
    () => `nimbalyst://tracker/${encodeURIComponent(itemId)}?orgId=${encodeURIComponent(team.orgId)}`,
    [itemId, team.orgId],
  );

  /**
   * The link a reader shares out of a browser tab.
   *
   * Absolute and without the list context the URL bar is carrying: `?type=` and
   * `?view=` record which list this item was opened from, which is this
   * reader's navigation and not part of the item's address. Desktop's copy-link
   * hands over a `nimbalyst://` deep link because that is what its recipient can
   * open; here the recipient has a browser, so this one does too. The desktop
   * handoff is still on the row, on the status chip beside it.
   */
  const shareHref = useMemo(
    () => new URL(orgTrackerItemPath(team, projectId, itemId), window.location.origin).toString(),
    [team, projectId, itemId],
  );

  if (!item) {
    // "Not here yet", "not in this room", and "this tab cannot say" look
    // identical on screen, so the difference is stated rather than guessed at.
    // Only a loaded snapshot over a connected room is an answer; a loaded
    // snapshot over a dead socket is a stale local copy that may simply be
    // missing an item the room has, and reporting it as deleted would be a
    // guess dressed as a fact.
    if (loaded && sync.status === 'connected') {
      return (
        <TrackerSurfaceMessage
          icon="search_off"
          message="This item is not in this tracker room."
          hint="It may have been deleted, or it may belong to another project."
          testId="tracker-item-missing"
        />
      );
    }
    if (loaded && (sync.status === 'disconnected' || sync.status === 'error')) {
      return (
        <TrackerSurfaceMessage
          icon="cloud_off"
          message="This item is not in the copy this tab holds."
          hint="The tracker room is not connected, so whether it still exists cannot be answered here."
          testId="tracker-item-indeterminate"
        />
      );
    }
    return (
      <TrackerSurfaceMessage icon="hourglass_top" message="Opening this item…" testId="tracker-item-pending" />
    );
  }

  const itemRejection = rejection?.itemId === itemId ? rejection : null;

  const overflowActions: TrackerItemAction[] = [
    ...(item.issueKey ? [{
      id: 'copy-key',
      label: 'Copy item key',
      icon: 'tag',
      onSelect: () => copyValue(item.issueKey!, `${item.issueKey} copied`),
    }] : []),
    {
      id: 'copy-title',
      label: 'Copy title',
      icon: 'title',
      onSelect: () => copyValue(getRecordTitle(item), 'Title copied'),
    },
  ];

  // Fills the detail pane the shell gives it; the pane owns the width.
  return (
    <div className="tracker-item-surface flex h-full min-h-0 min-w-0 flex-col">
      <TrackerMutationRejectionNotice rejection={itemRejection} />
      {mutationError ? (
        <div className="tracker-item-mutation-error px-3 py-2 text-xs text-nim-error" role="alert">
          {mutationError}
        </div>
      ) : null}
      {actionNotice ? (
        <div className="tracker-item-action-notice px-3 py-2 text-xs text-nim-muted" role="status">
          {actionNotice}
        </div>
      ) : null}
      {/*
        Hosts the body's connection state so the panel can draw it on the header
        row. Without this the editor finds no sink and falls back to its own
        full-width strip, which is where "Open in desktop" used to spend a row
        of its own directly above the document.
      */}
      <CollabDocumentStatusProvider>
        <TrackerItemDetailPanel
          item={item}
          identity={identity}
          compact={phone}
          editing={editing}
          onEditingChange={next => setEditState({ itemId, editing: next })}
          backLabel={backLabel}
          commentDraft={commentDraft}
          onCommentDraftChange={setCommentDraft}
          onFieldChange={updateField}
          commentMutate={mutateComment}
          mutationRejection={itemRejection}
          formatTimestamp={(createdAt) => new Date(createdAt).toLocaleString()}
          teamMembers={teamMembers}
          headerStatus={<ItemBodyStatus />}
          copyLinkHref={shareHref}
          overflowActions={overflowActions}
          bodySlot={<CollabEditorMount source={source} user={user} desktopHref={desktopHref} readOnly={!editing} />}
          onClose={() => navigate(closePath)}
        />
      </CollabDocumentStatusProvider>
    </div>
  );
}
